import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import {
  XMarkIcon,
  PhotoIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline'
import { bourbonApi, CreateBourbonListingData } from '../services/bourbonApi'
import PhotoUpload from './PhotoUpload'
import toast from 'react-hot-toast'

interface CreateBourbonListingModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function CreateBourbonListingModal({
  isOpen,
  onClose,
  onSuccess
}: CreateBourbonListingModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateBourbonListingData>({
    type: 'have',
    title: '',
    brand: '',
    age: '',
    proof: undefined,
    category: 'bourbon',
    description: '',
    images: [],
    location: '',
    tradePreferences: ''
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.category) {
      newErrors.category = 'Category is required'
    }

    // Validate age if it's a number
    if (formData.age && typeof formData.age === 'number' && (formData.age < 0 || formData.age > 50)) {
      newErrors.age = 'Age must be between 0 and 50 years'
    }

    if (formData.proof && (formData.proof < 0 || formData.proof > 200)) {
      newErrors.proof = 'Proof must be between 0 and 200'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      await bourbonApi.createListing(formData, getAccessTokenSilently)
      onSuccess()
      
      // Reset form
      setFormData({
        type: 'have',
        title: '',
        brand: '',
        age: '',
        proof: undefined,
        category: 'bourbon',
        description: '',
        images: [],
        location: '',
        tradePreferences: ''
      })
      setErrors({})
    } catch (error: any) {
      console.error('Create listing error:', error)
      toast.error(error.message || 'Failed to create listing')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePhotoUploaded = (photoUrl: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...(prev.images || []), photoUrl]
    }))
  }

  const handlePhotoRemoved = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }))
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl relative z-[61]"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Create Bourbon Listing
              </h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Listing Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'have' }))}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData.type === 'have'
                        ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-lg font-semibold mb-1">I HAVE</div>
                    <div className="text-sm">Offering to trade/sell</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'want' }))}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData.type === 'want'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-lg font-semibold mb-1">I WANT</div>
                    <div className="text-sm">Looking to acquire</div>
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Pappy Van Winkle 15yr"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              {/* Brand and Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Brand
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                    placeholder="e.g. Buffalo Trace"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="bourbon">Bourbon</option>
                    <option value="whiskey">Whiskey</option>
                    <option value="rye">Rye</option>
                    <option value="scotch">Scotch</option>
                    <option value="irish">Irish</option>
                    <option value="japanese">Japanese</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                </div>
              </div>

              {/* Age and Proof */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Age
                  </label>
                  <input
                    type="text"
                    value={formData.age || ''}
                    onChange={(e) => {
                      const value = e.target.value.trim()
                      // Try to parse as number, otherwise keep as string
                      const numValue = parseFloat(value)
                      setFormData(prev => ({ 
                        ...prev, 
                        age: value === '' ? undefined : (!isNaN(numValue) && value === numValue.toString() ? numValue : value)
                      }))
                    }}
                    placeholder="e.g. 12, NAS, 18 months"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter years (e.g. 12) or text (e.g. NAS, 18 months)
                  </p>
                  {errors.age && <p className="mt-1 text-sm text-red-600">{errors.age}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Proof
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="200"
                    step="0.1"
                    value={formData.proof || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      proof: e.target.value ? parseFloat(e.target.value) : undefined 
                    }))}
                    placeholder="e.g. 107.5"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {errors.proof && <p className="mt-1 text-sm text-red-600">{errors.proof}</p>}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the condition, notes, or any additional details..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g. Louisville, KY"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Trade Preferences */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {formData.type === 'have' ? 'What are you looking for?' : 'What can you offer in return?'}
                </label>
                <textarea
                  rows={3}
                  value={formData.tradePreferences}
                  onChange={(e) => setFormData(prev => ({ ...prev, tradePreferences: e.target.value }))}
                  placeholder={formData.type === 'have' 
                    ? "e.g. Looking for Blanton's, Eagle Rare, or similar bourbon..."
                    : "e.g. Can trade Weller Special Reserve, or willing to pay cash..."
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Photos
                </label>
                <div className="space-y-4">
                  {/* Existing Photos */}
                  {formData.images && formData.images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {formData.images.map((imageUrl, index) => (
                        <div key={index} className="relative">
                          <img
                            src={imageUrl}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => handlePhotoRemoved(index)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <MinusIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Photo */}
                  {(!formData.images || formData.images.length < 5) && (
                    <PhotoUpload
                      onPhotoUploaded={handlePhotoUploaded}
                      onPhotoRemoved={() => {}} // Not used in this context
                      className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4"
                    />
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    You can upload up to 5 photos. Photos help build trust and show condition.
                  </p>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Listing'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  )
}
